import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { performLogout } from '../utils/logout';

const SelectAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Utiliser le domaine courant pour les appels API en production
  const apiBase = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!token) {
      setError('Lien invalide');
      setLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(
        `${apiBase}/api/auth/validate-magic-link/${token}`
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Lien invalide ou expiré');
      }

      const data = await response.json();
      setAccounts(data.accounts);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAccountSelect = async (accountId) => {
    try {
      const response = await fetch(
        `${apiBase}/api/auth/magic-login/${accountId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la connexion');
      }

      const data = await response.json();
      
      // Récupérer le profil complet de l'utilisateur (incluant is_moderator)
      const profileResponse = await fetch(`${apiBase}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${data.access_token}`
        }
      });
      
      let userProfile = null;
      if (profileResponse.ok) {
        userProfile = await profileResponse.json();
      }
      
      // Trouver le compte sélectionné pour récupérer l'email
      const selectedAccount = accounts.find(acc => acc.id === accountId);
      const displayName = selectedAccount ? `${selectedAccount.first_name} ${selectedAccount.last_name}` : 'Membre';
      const memberEmail = selectedAccount?.email || userProfile?.email || '';
      const memberFullName = selectedAccount 
        ? `${selectedAccount.first_name || ''} ${selectedAccount.last_name || ''}`.trim()
        : '';
      
      // Sauvegarder les tokens comme le fait handleLogin dans EspaceMembre
      localStorage.setItem('authToken', data.access_token);
      localStorage.setItem('memberToken', data.access_token); // Compatibilité
      localStorage.setItem('memberEmail', memberEmail); // Email pour inscription événements
      localStorage.setItem('memberFullName', memberFullName); // Nom pour pré-remplir formulaires
      localStorage.setItem('currentUser', JSON.stringify({
        id: userProfile?.id || accountId,
        email: memberEmail,
        role: data.user_role,
        name: data.user_role === 'admin' ? 'Administrateur (Mode Membre)' : 'Membre',
        first_name: selectedAccount?.first_name || '',
        last_name: selectedAccount?.last_name || '',
        token: data.access_token,
        is_moderator: userProfile?.is_moderator || false
      }));
      
      // Rediriger vers l'espace membre
      navigate('/membre/dashboard');
    } catch (err) {
      setError(err.message);
    }
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      'admin': 'Administrateur',
      'membre_vie': 'Membre à vie',
      'membre_annuel': 'Membre annuel',
      'membre_exterieur': 'Membre extérieur',
      'decede': 'Décédé',
      'prospect': 'Prospect',
      'non_actif': 'Non-actif'
    };
    return roleLabels[role] || role;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(196,150,96)] mx-auto"></div>
          <p className="mt-4 text-gray-600">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  const handleLogoutAndReturn = () => {
    performLogout();
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-red-600">Erreur</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">{error}</p>
            <Button 
              onClick={handleLogoutAndReturn}
              style={{ backgroundColor: 'rgb(196, 150, 96)' }}
              className="w-full text-white"
            >
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <img 
            src="/images/lignages-logo-admin.png" 
            alt="Logo Lignages" 
            className="mx-auto h-20 w-20 object-contain"
          />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900" style={{ fontFamily: '"Lora", serif' }}>
            Sélectionnez votre compte
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {accounts.length > 1 
              ? `${accounts.length} comptes sont associés à votre adresse email`
              : 'Cliquez sur votre compte pour vous connecter'
            }
          </p>
        </div>

        <div className="flex justify-center items-center">
          <div className="w-full max-w-sm px-4">
            {accounts.map((account) => (
              <Card 
                key={account.id}
                className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-[rgb(196,150,96)] mb-4"
                onClick={() => handleAccountSelect(account.id)}
              >
                <CardContent className="p-6">
                  <div className="text-center">
                    {/* Avatar placeholder */}
                    <div 
                      className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
                      style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                    >
                      {account.first_name?.[0]}{account.last_name?.[0]}
                    </div>
                    
                    {/* Nom complet */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: '"Lora", serif' }}>
                      {account.full_name}
                    </h3>
                    
                    {/* Type de membre */}
                    <p className="text-sm text-gray-600 mb-4">
                      {getRoleLabel(account.role)}
                    </p>
                    
                    {/* Bouton */}
                    <Button 
                      className="w-full text-white"
                      style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                    >
                      Se connecter
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/espace-membre')}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Retour à la page de connexion
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectAccount;
